import { useState, useEffect } from "react";
import { apiClient } from "../../apiClient";
import AppSelect from "../ui/AppSelect";
import debounce from "lodash.debounce";
import { Contatto } from "../../types/index";
import linkedin from "../../assets/linkedin.svg";
import cuoreVuoto from "../../assets/heart-empty.svg";
import cuorePieno from "../../assets/heart-full.svg";
import cestino from "../../assets/delete.svg";
import info from "../../assets/info.svg";
import AddLeadDiv from "../ui/Leads/AddLeads";
import { useAuth } from '../../context/AuthContext';
import DettagliContattoModal from "../ui/Leads/DettagliContattoModal";
import { hasText, isLeadDataComplete } from "../../utils/leadCompleteness";
import { LeadCard, PanelFilterBar, PanelHero } from "../ui/panel";


interface Option {
  value: string;
  label: string;
}

type TipoContatto = "contatto" | "small-lead" | "startup";

export default function ContattiView() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [contatti, setContatti] = useState<Contatto[]>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ nome: "", associato: "", servizio: "", esito: "", tipo: "" });
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [nomeInput, setNomeInput] = useState("");
  const [showAddLead, setShowAddLead] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  // Stato per gestire il contatto selezionato per il blocco dettagli
  const [selectedContact, setSelectedContact] = useState<Contatto | null>(null);

  const [options, setOptions] = useState<{ associati: Option[], servizi: Option[], esiti: Option[] }>({
    associati: [], servizi: [], esiti: []
  });

  const tipoOptions: Option[] = [
    { value: "", label: "Tutti" },
    { value: "contatto", label: "Linkedin" },
    { value: "small-lead", label: "Small Leads" },
    { value: "startup", label: "Startup Toscane" },
  ];

  const debouncedSetNome = debounce((value: string) => {
    setFilters((prev) => ({ ...prev, nome: value }));
  }, 500);

  const fetchOptions = async () => {
    try {
      const [associati, servizi, esiti] = await Promise.all([
        apiClient.get("json/associati"),
        apiClient.get("json/servizi"),
        apiClient.get("json/esiti"),
      ]);
      setOptions({ associati: associati.data, servizi: servizi.data, esiti: esiti.data });
    } catch (err) { console.error("Errore opzioni:", err); }
  };

  const fetchContatti = async (append = false, filt = filters) => {
    setLoading(true);
    try {
      const res = await apiClient.get("contatti/", {
        params: {
          nome: filt.nome || undefined,
          associato: isAdmin ? (filt.associato || undefined) : undefined,
          servizio: filt.servizio || undefined,
          esito: filt.esito || undefined,
          tipo: filt.tipo || undefined,
          skip: append ? page * 25 : 0,
          limit: 25,
        },
      });
      const nuovi = (res.data.contatti || []).map((c: Contatto) => ({
        ...c,
        preferito: c.preferito ?? false,
        numero_contatti:
          c.data_ultimo_contatto && (c.numero_contatti ?? 0) === 0 ? 1 : (c.numero_contatti ?? 0),
      }));
      setContatti((prev) => (append ? [...prev, ...nuovi] : nuovi));
      setHasMore(nuovi.length === 25);
    } catch (err) { console.error("Errore contatti:", err); } 
    finally { setLoading(false); }
  };

  useEffect(() => { fetchOptions(); }, []);
  useEffect(() => { setPage(0); fetchContatti(false); }, [filters]);
  useEffect(() => { if (page > 0) fetchContatti(true); }, [page]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.innerHeight + window.scrollY >= document.body.offsetHeight - 300 && !loading && hasMore) {
        setPage((prev) => prev + 1);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [loading, hasMore]);

  const togglePreferito = async (id: number, attuale: boolean, tipo: TipoContatto) => {
    try {
      setContatti(prev => prev.map(c => (c.id === id && c.tipo === tipo ? { ...c, preferito: !attuale } : c)));
      await apiClient.put(`contatti/${id}/preferito?tipo=${tipo}`, { preferito: !attuale });
    } catch (err) { console.error("Errore preferito:", err); }
  };

  const eliminaContatto = async (id: number, tipo: TipoContatto) => {
    try {
      await apiClient.delete(`deleteContatto/${id}?tipo=${tipo}`);
      setContatti(prev => prev.filter(c => !(c.id === id && c.tipo === tipo)));
      setDeleteConfirmId(null);
    } catch (err) { console.error("Errore eliminazione:", err); }
  };

  const handleContactSaved = (updated: Contatto) => {
    setContatti((prev) =>
      prev.map((item) => (item.id === updated.id && item.tipo === updated.tipo ? { ...item, ...updated } : item))
    );
    setSelectedContact(updated);
  };

  return (
    <div className="space-y-6 pb-10">
      <PanelHero
        variant="brand"
        eyebrow="CRM"
        title="Contatti"
        description="Gestisci i tuoi lead e le opportunità business."
        action={
          <button
            type="button"
            onClick={() => setShowAddLead(true)}
            className="w-full rounded-xl bg-gradient-to-r from-[#cd212a] cursor-pointer to-[#a01820] px-6 py-2.5 text-sm font-extrabold text-white shadow-lg shadow-primary/25 transition hover:shadow-xl md:w-auto"
          >
            + Nuovo Lead
          </button>
        }
      />

      {showAddLead && (
        <AddLeadDiv
          preassignedTo={user?.username}
          onClose={() => {
            setShowAddLead(false);
            setPage(0);
            fetchContatti(false);
          }}
        />
      )}

      <PanelFilterBar>
        <input
          type="text"
          placeholder="Cerca per nome o azienda..."
          className="min-w-[220px] flex-1 rounded-xl border border-secondary/15 bg-secondary/[0.02] px-3 py-2.5 text-sm outline-none focus:border-primary/40 focus:ring-2 focus:ring-primary/15"
          value={nomeInput}
          onChange={(e) => {
            const value = e.target.value.trimStart();
            setNomeInput(value);
            debouncedSetNome(value);
          }}
        />
        {isAdmin && (
          <div className="w-full sm:w-44">
            <AppSelect
              options={options.associati}
              placeholder="Assegnato"
              isClearable
              onChange={(o) => setFilters((p) => ({ ...p, associato: o?.value ?? "" }))}
              className="w-full sm:w-44"
            />
          </div>
        )}
        <div className="w-full sm:w-44">
          <AppSelect
            options={options.servizi}
            placeholder="Servizio"
            isClearable
            onChange={(o) => setFilters((p) => ({ ...p, servizio: o?.value ?? "" }))}
            className="w-full sm:w-44"
          />
        </div>
        <div className="w-full sm:w-44">
          <AppSelect
            options={options.esiti}
            placeholder="Esito"
            isClearable
            onChange={(o) => setFilters((p) => ({ ...p, esito: o?.value ?? "" }))}
            className="w-full sm:w-44"
          />
        </div>
        <div className="w-full sm:w-44">
          <AppSelect
            options={tipoOptions}
            placeholder="Tipo Lead"
            isClearable
            onChange={(o) => setFilters((p) => ({ ...p, tipo: o?.value ?? "" }))}
            className="w-full sm:w-44"
          />
        </div>
      </PanelFilterBar>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
        {contatti.map((c) => {
          const badgeStyles = {
            contatto: "bg-blue-50 text-blue-700 border-blue-100",
            "small-lead": "bg-amber-50 text-amber-700 border-amber-100",
            startup: "bg-purple-50 text-purple-700 border-purple-100"
          }[c.tipo];

          const label = { contatto: "Linkedin", "small-lead": "Small Lead", startup: "Startup" }[c.tipo];
          const referenteStartup = `${c.nome || ""} ${c.cognome || ""}`.trim();
          const nomeCard = c.azienda || `${c.nome || ""} ${c.cognome || ""}`.trim() || "Lead senza nome";
          const showIncompleteBadge = hasText(c.associato) && !isLeadDataComplete(c);
          const incompleteBadgeLabel = isAdmin
            ? `✓ Preso in carico da ${c.associato} - completa dati`
            : "✓ Preso in carico - completa dati";

          return (
            <LeadCard
              key={`${c.tipo}-${c.id}`}
              highlight={showIncompleteBadge ? "amber" : "none"}
            >
              <div>
                <div className="flex justify-between items-start mb-3">
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-md border ${badgeStyles}`}>
                      {label}
                    </span>
                    {showIncompleteBadge && (
                      <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-md border bg-amber-50 text-amber-700 border-amber-200">
                        {incompleteBadgeLabel}
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                     {c.linkedin_url && (
                        <a href={c.linkedin_url} target="_blank" rel="noreferrer" className="cursor-pointer">
                          <img src={linkedin} className="w-4 h-4 grayscale hover:grayscale-0 transition" alt="Link" />
                        </a>
                     )}
                  </div>
                </div>
                <h3 className="mb-1 truncate text-lg font-extrabold leading-tight text-secondary">
                  {nomeCard}
                </h3>
                {c.tipo === "startup" && referenteStartup && (
                  <p className="text-xs text-gray-400">Referente: {referenteStartup}</p>
                )}
              </div>

              <div className="mt-6 pt-4 border-t border-gray-50 flex items-center justify-between">
                <button 
                  onClick={() => setSelectedContact(c)}
                  className="flex items-center gap-1.5 text-xs font-bold text-primary hover:text-[#a01820] transition cursor-pointer"
                >
                  <img src={info} className="w-4 h-4" alt="info" />
                  DETTAGLI
                </button>

                <div className="flex items-center gap-3">
                  <img
                    src={c.preferito ? cuorePieno : cuoreVuoto}
                    className="w-5 h-5 cursor-pointer hover:scale-110 transition"
                    onClick={() => togglePreferito(c.id, !!c.preferito, c.tipo as TipoContatto)}
                    alt="fav"
                  />
                  {user?.role === 'admin' && (
                    deleteConfirmId === c.id ? (
                      <button onClick={() => eliminaContatto(c.id, c.tipo as TipoContatto)} className="text-[10px] font-bold text-red-600 bg-red-50 px-2 py-1 rounded">CONFERMA</button>
                    ) : (
                      <img src={cestino} className="w-5 h-5 cursor-pointer opacity-40 hover:opacity-100 transition" onClick={() => setDeleteConfirmId(c.id)} alt="del" />
                    )
                  )}
                </div>
              </div>
            </LeadCard>
          );
        })}
      </div>
      {loading && (
        <div className="animate-pulse py-10 text-center text-sm font-medium text-secondary/40">
          Caricamento altri contatti...
        </div>
      )}
      {selectedContact && (
        <DettagliContattoModal 
          contatto={selectedContact} 
          onClose={() => setSelectedContact(null)} 
          onSave={handleContactSaved}
        />
      )}
    </div>
  );
}